/**
 * useChatSend — Core message sending logic.
 * Handles: LLM call, twin signals, pulse tick, curiosity check, action parsing.
 * Rule 94: Modüler — mesaj gönderme tek hook'ta.
 */
import { useCallback, useRef, useState } from 'react'
import { API_URL, apiFetch } from '../../../config/api'
import type { ChatMessage } from '../../../contexts/ChatContext'
import { dispatchAgentActions, handleBackendActions, parseAgentActions, stripActionTags } from './useAgentActions'

interface UseChatSendOptions {
  addMessage: (msg: ChatMessage) => void
  updateLastMessage: (updates: Partial<ChatMessage>) => void
  agentId: number | string | undefined
  agentName: string | undefined
  speak: (text: string) => void
}

export function useChatSend({ addMessage, updateLastMessage, agentId, agentName, speak }: UseChatSendOptions) {
  const [sending, setSending] = useState(false)
  const messageCountRef = useRef(0)

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || sending) return
    const agent_id = agentId && Number(agentId) > 0 ? Number(agentId) : 0

    addMessage({ role: 'user', content: text, ts: new Date().toLocaleTimeString() })
    addMessage({
      role: 'assistant', content: '...', ts: new Date().toLocaleTimeString(),
      agentName: agentName, status: 'thinking',
    })
    setSending(true)

    try {
      // Twin Signal Observer — fire & forget
      apiFetch(`${API_URL}/api/twin/signals`, {
        method: 'POST',
        body: JSON.stringify({ user_id: parseInt(localStorage.getItem('user_id') || '0'), message: text }),
        headers: { 'Content-Type': 'application/json' },
      }).catch(() => {})

      const res = await apiFetch(`${API_URL}/api/user-chat/send`, {
        method: 'POST',
        body: JSON.stringify({
          agent_id,
          message: text,
          active_windows: Array.from(document.querySelectorAll('[data-window-id]')).map(el => el.getAttribute('data-window-id')).filter(Boolean),
          active_context: (() => {
            try { return JSON.parse(localStorage.getItem('runai_active_context') || '{}') } catch { return {} }
          })(),
        }),
        headers: { 'Content-Type': 'application/json' },
      })

      const data = await res.json() as {
        response?: string; error?: string; plan?: string
        limit?: number; used?: number; message?: string
        actions?: string[]; eq_score?: number
      }

      // Plan limit check
      if (data.error === 'plan_limit_exceeded') {
        updateLastMessage({
          content: `⚠️ Monthly limit reached (${data.used ?? '?'}/${data.limit ?? '?'}). Upgrade your plan for more.`,
          status: 'done',
        })
        return
      }

      let reply = data.response ?? ''

      // Parse action tags from LLM response
      const parsedActions = parseAgentActions(reply)
      if (parsedActions.length > 0) {
        dispatchAgentActions(parsedActions)
        reply = stripActionTags(reply)
      }

      // Handle backend JSON actions
      handleBackendActions(data.actions)

      updateLastMessage({ content: reply, status: 'done', eq_score: data.eq_score })
      speak(reply)

      // Twin Curiosity Check — her 10 mesajda soru sor
      messageCountRef.current++
      if (messageCountRef.current % 10 === 0) {
        try {
          const throttle = await apiFetch(`${API_URL}/api/twin/curiosity/throttle`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
          }).then(r => r.json()) as { remaining?: number }
          if (throttle?.remaining && throttle.remaining > 0) {
            const followUp = await apiFetch(`${API_URL}/api/twin/curiosity/follow-up`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
            }).then(r => r.json()) as { question?: string }
            if (followUp?.question) {
              setTimeout(() => {
                addMessage({
                  role: 'assistant',
                  content: `🧬 ${followUp.question}`,
                  ts: new Date().toLocaleTimeString(),
                  agentName: 'Twin',
                  status: 'done',
                })
              }, 2000)
            }
          }
        } catch { /* curiosity check non-critical */ }
      }

      // Pulse Tick — her 5 mesajda agent proaktif sunum
      if (messageCountRef.current % 5 === 0) {
        try {
          const pulseRes = await apiFetch(`${API_URL}/api/twin/pulse/tick`, {
            method: 'POST', body: JSON.stringify({}), headers: { 'Content-Type': 'application/json' },
          })
          const pulse = (await pulseRes.json())?.pulse
          if (pulse?.suggestion_for_user) {
            setTimeout(() => {
              addMessage({
                role: 'assistant',
                content: `💓 ${pulse.suggestion_for_user}`,
                ts: new Date().toLocaleTimeString(),
                agentName: 'Pulse',
                status: 'done',
              })
            }, 4000)
          }
        } catch { /* pulse non-critical */ }
      }
    } catch {
      updateLastMessage({ content: '⚠ Connection error.', status: 'done' })
    } finally {
      setSending(false)
    }
  }, [sending, agentId, agentName, addMessage, updateLastMessage, speak])

  return { sending, sendMessage }
}
